import { useCallback, useEffect, useState } from 'react';
import { match } from 'react-router-dom';
import { DoctorAppointmentProfileView } from '../../ConfirmAppointment/DoctorAppointmentProfileView';

import * as api from '../../../api';
import * as logger from '../../../logger';

import loginStyles from '../../Login/index.module.css';
import LoadingBar from '../../../components/LoadingBar';
import { InfoMessage } from '../../../components/InfoMessage';
import { Button } from '../../../components/Button';
import { ModifyAppointmentModal } from '../../ConfirmAppointment/ModifyAppointmentModal';
import { PhoneNumberView } from '../../ConfirmAppointment/PhoneNumberView';
import { RescheduleView } from '../../ConfirmAppointment/RescheduleView';

import styles from './index.module.css';

interface Props {
  match: match<{ ehrTimeSlotBookingId: string }>;
}

const AppointmentAction: React.FC<Props> = ({ match }) => {
  const { ehrTimeSlotBookingId } = match.params;
  const [appointmentConfirmation, setAppointmentConfirmation] = useState<
    api.EhrTimeSlot.TimeSlotConfirmation | undefined
  >();
  const [loading, setLoading] = useState(true);
  const [loadingText, setLoadingText] = useState('Loading...');
  const [cancelling, setCancelling] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [canReschedule, setCanReschedule] = useState<boolean>(false);
  const [canceled, setCanceled] = useState<boolean>(false);
  const [rescheduled, setRescheduled] = useState<boolean>(false);
  const [slot, setSlot] = useState<api.EhrTimeSlot.EhrTimeSlot>();

  const refreshAppointmentConfirmation = useCallback(async () => {
    if (!appointmentConfirmation) {
      try {
        const confirmation =
          await api.client.EhrTimeSlotBooking.getBookingInfo(
            ehrTimeSlotBookingId,
          );
        setAppointmentConfirmation(confirmation);
        setCanReschedule(confirmation.rescheduleAppointmentKind !== undefined);
        setErrorMessage('');
      } catch (err) {
        logger.error(err, 'Failed to load appointment');
        setErrorMessage('Failed to load the appointment');
      } finally {
        setLoading(false);
      }
    }
  }, [ehrTimeSlotBookingId, appointmentConfirmation]);

  const setInformationMessage = useCallback(async () => {
    if (!canReschedule) {
      setInfoMessage('Are you sure you want to cancel your appointment?');
      return;
    }
    setInfoMessage(
      'Select a new time for your appointment below, or cancel if you can no longer attend.',
    );
  }, [canReschedule, setInfoMessage]);

  async function cancelAppointment() {
    try {
      setLoading(true);
      setLoadingText('Cancelling appointment...');
      await api.client.EhrTimeSlotBooking.cancelBooking(ehrTimeSlotBookingId);
      await refreshAppointmentConfirmation();
      setErrorMessage('');
      setInfoMessage('All done! Your appointment has been canceled.');
      setCanceled(true);
      setRescheduled(false);
    } catch (err) {
      logger.error(err, 'Failed to cancel appointment');
      setErrorMessage('Failed to cancel the appointment');
    } finally {
      setCancelling(false);
      setLoading(false);
    }
  }

  async function rescheduleAppointment() {
    setInfoMessage('All done! Your appointment has been rescheduled.');
    setCanceled(false);
    setRescheduled(true);
    await refreshAppointmentConfirmation();
    setLoading(false);
  }

  const [cancelConfirmationModalOpen, setCancelConfirmationModalOpen] =
    useState(false);
  const cancelConfirmModal = (
    <ModifyAppointmentModal
      title="Cancel Appointment"
      instructions="Are you sure you want to cancel this appointment? This action cannot be undone."
      isOpen={cancelConfirmationModalOpen}
      setModalOpen={setCancelConfirmationModalOpen}
      onModifyAppointment={() => cancelAppointment()}
    />
  );
  const cancelButton = (
    <Button
      className={styles.ConfirmCancelButton}
      type="button"
      onClick={() => setCancelConfirmationModalOpen(true)}
      children="Cancel appointment"
      disabled={loading || cancelling}
      invertColors={true}
    />
  );

  useEffect(() => {
    refreshAppointmentConfirmation();
    setInformationMessage();
  }, [refreshAppointmentConfirmation, setInformationMessage]);

  if (loading) return <LoadingBar label={loadingText} />;

  if (!appointmentConfirmation) {
    return <InfoMessage isError={true}>Failed to load appointment</InfoMessage>;
  }

  if (
    appointmentConfirmation.cancellationSuccess &&
    !canceled &&
    !rescheduled
  ) {
    return (
      <InfoMessage isError={false}>
        All done! No other action needed.
      </InfoMessage>
    );
  }

  if (canceled || rescheduled) {
    return (
      <div>
        <div className={styles.Instructions}>
          {errorMessage ? (
            <InfoMessage isError={true}>{errorMessage}</InfoMessage>
          ) : null}
          {infoMessage}
        </div>
        <DoctorAppointmentProfileView
          id={ehrTimeSlotBookingId}
          startTime={slot ? slot.startTime : appointmentConfirmation.startTime}
          provider={{
            prefix: appointmentConfirmation.providerPrefix,
            suffix: appointmentConfirmation.providerSuffix,
            firstName: appointmentConfirmation.providerFirstName,
            lastName: appointmentConfirmation.providerLastName,
            previsitImageUrl: appointmentConfirmation.previsitImageUrl,
            virtualVisitUrl: null,
            sex: null,
          }}
          practice={appointmentConfirmation}
          status={
            canceled ? api.EhrTimeSlot.AppointmentStatus.Cancelled : undefined
          }
        />
        {canceled && appointmentConfirmation.phoneNumber && !canReschedule ? (
          <InfoMessage>
            <span>
              To reschedule an appointment or receive help with a cancellation,
              give us a call at{' '}
            </span>
            <PhoneNumberView
              phoneNumber={appointmentConfirmation.phoneNumber}
              className={loginStyles.InlineBlock}
            />
          </InfoMessage>
        ) : null}
      </div>
    );
  }
  return (
    <div>
      <div className={styles.Instructions}>
        {errorMessage ? (
          <InfoMessage isError={true}>{errorMessage}</InfoMessage>
        ) : null}
        {infoMessage}
      </div>
      <DoctorAppointmentProfileView
        id={ehrTimeSlotBookingId}
        startTime={appointmentConfirmation.startTime}
        provider={{
          prefix: appointmentConfirmation.providerPrefix,
          suffix: appointmentConfirmation.providerSuffix,
          firstName: appointmentConfirmation.providerFirstName,
          lastName: appointmentConfirmation.providerLastName,
          previsitImageUrl: appointmentConfirmation.previsitImageUrl,
          virtualVisitUrl: null,
          sex: null,
        }}
        practice={appointmentConfirmation}
        status={
          appointmentConfirmation.cancellationSuccess
            ? api.EhrTimeSlot.AppointmentStatus.Cancelled
            : undefined
        }
        actionInfoContext={
          <>{!appointmentConfirmation.cancellationSuccess && cancelButton}</>
        }
      />
      {canReschedule ? (
        <RescheduleView
          rescheduleIdentifier={{
            ehrTimeSlotBookingId: ehrTimeSlotBookingId,
          }}
          setIsRescheduling={setLoading}
          onRescheduled={() => rescheduleAppointment()}
          setSlot={setSlot}
          timezone={appointmentConfirmation.timezone}
        />
      ) : null}
      {cancelConfirmModal}
    </div>
  );
};

export default AppointmentAction;
